"use client";

import { memo } from "react";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { Spinner } from "@/components/ui/spinner";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

export const PdfDocumentTabs = memo(function PdfDocumentTabs() {
  const { t } = useTranslation();
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const setActiveDocument = usePdfDocumentStore((s) => s.setActiveDocument);
  const closeDocument = usePdfDocumentStore((s) => s.closeDocument);
  const closeOtherDocuments = usePdfDocumentStore((s) => s.closeOtherDocuments);
  const closeAllDocuments = usePdfDocumentStore((s) => s.closeAllDocuments);

  if (documents.length === 0) return null;

  return (
    <div
      className="flex flex-wrap max-h-16 overflow-hidden border-b border-border/30 bg-muted/20 px-1 pt-1"
      role="tablist"
    >
      {documents.map((doc) => {
        const isActive = doc.id === activeDocumentId;
        return (
          <ContextMenu key={doc.id}>
            <ContextMenuTrigger asChild>
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                title={doc.title}
                onClick={() => setActiveDocument(doc.id)}
                onAuxClick={(e) => {
                  if (e.button === 1) closeDocument(doc.id);
                }}
                className={cn(
                  "group flex items-center gap-1.5 px-3 h-7 rounded-t-lg text-xs transition-all select-none shrink-0 max-w-48",
                  isActive
                    ? "bg-background text-foreground font-medium shadow-sm border border-b-0 border-border/30"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
                )}
              >
                {doc.loading && (
                  <Spinner className="shrink-0 text-muted-foreground" />
                )}
                <span className="truncate">{doc.title}</span>
                <span
                  role="button"
                  tabIndex={-1}
                  aria-label={`Close ${doc.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeDocument(doc.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.stopPropagation();
                      closeDocument(doc.id);
                    }
                  }}
                  className="ml-auto flex size-4 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-foreground/10 cursor-pointer opacity-0 group-hover:opacity-100"
                >
                  <XIcon className="size-3" />
                </span>
              </button>
            </ContextMenuTrigger>

            <ContextMenuContent>
              <ContextMenuItem onSelect={() => closeDocument(doc.id)}>
                {t("pdf.close")}
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => closeOtherDocuments(doc.id)}
                disabled={documents.length <= 1}
              >
                {t("pdf.closeOthers")}
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onSelect={closeAllDocuments}>
                {t("pdf.closeAll")}
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        );
      })}
    </div>
  );
});
